/**
 * !!! IMPORTANT !!!
 *
 * NEVER IMPORT ANYTHING FROM THIS FILE DIRECTLY.
 * WRAP THE METHOD YOU NEED IN interactions.js
 *
 */

 import {
    betslipStore,
} from '~SpectateStore';

import {
    SELECTION_SELECTED_CLASS,
} from './constants';

import {
    SELECTION_GTM_ADD_BETSLIP,
    SELECTION_GTM_REMOVE_BETSLIP,
} from '~shared/components/selection/constants';

import {
    addSelection,
    addSelections,
    removeSelection,
    clearSelections,
} from '~Betslip/slices/userSelectionsSlice';

import {
    pinTab,
    updateOpenBets,
    decrementOpenBets,
    changeHeaderTab,
    setFullScreenOn,
    setFullScreenOff,
    triggerMaxSelectionsModal,
    closeModal,
    incrementRefreshReqCount,
    setBettingEnabled,
} from '~Betslip/slices/uiSlice';

import {
    selectUserSelectionIds,
    selectCountUserSelections,
} from '~Betslip/selectors/userSelectionsSelectors';

import {
    refreshBetslip,
} from '~Betslip/thunks/refreshBetslip';

import {
    refreshFreebets,
} from '~Betslip/thunks/refreshFreebets';

import {
    goBackToBetslip,
} from '~Betslip/slices/betReceiptSlice';

import {
    selectShowBetReceipt,
    selectBetReceiptExists,
} from '~Betslip/selectors/betReceiptSelectors';

import {
    selectIsMyBetsTab,
    selectUiMode,
} from '~Betslip/selectors/uiSelectors';

import {
    clearFreebets,
} from '~Betslip/utils/freeBetsActions';

import {
    findFcTcSelectionId,
} from '~Betslip/utils/selectionChecks';

import {
    selectBetslipMaxSelectionsConfig,
} from '~SpectateStore/selectors/clientStateSelectors';

import {
    setSiteScrollDisabled,
} from '~spa/Utils/WrapperIntegration/Ucf';

const TIMEOUT = 800;
let timer;

let timerIsRunning = false;

/**
 * Uses a timer to debounce the refreshBetslip action
 *
 * If a user adds/removes several selections with less than 800ms between clicks
 * then it will send a single request that collected all the changes
 * @returns {void} nothing
 */
const fetchBetslipData = () => {
    clearTimeout(timer);
    /**
     * If timeIsRunning it means other selection was added/removed
     * recenlty and we are in debounce-time
     */
    if (!timerIsRunning) {
        // First added removed in this debounce-time
        betslipStore.dispatch(incrementRefreshReqCount());
    }
    timerIsRunning = true;
    timer = setTimeout(
        () => {
            timerIsRunning = false;
            betslipStore.dispatch(refreshBetslip());
        },
        TIMEOUT
    );
};

export const fetchBetslipFreebets = () => {
    betslipStore.dispatch(refreshFreebets());
};

export const clearBetslipFreebets = () => {
    betslipStore.dispatch(clearFreebets());
};

export const initBetslip = () => {
    const addedSelectionsCount = selectCountUserSelections(
        betslipStore.getState(),
    );
    if (addedSelectionsCount > 0) {
        betslipStore.dispatch(incrementRefreshReqCount());
    }

    betslipStore.dispatch(
        refreshBetslip()
    );
};

const toggleSelection = (selectionId, isOn) => {
    // If the selectionId is not given - fallback to all selections in Betslip
    const selector = selectionId
        ? `.bb-sport-event__selection[data-selection-id="${selectionId}"]`
        : `.${SELECTION_SELECTED_CLASS}`;

    document
        .querySelectorAll(selector)
        .forEach((button) => {
            // temporary solution for BT-10640
            // will handle this correctly on BT-10643
            const { spectateProduct } = button.dataset;
            button.classList.toggle(SELECTION_SELECTED_CLASS, isOn);
            if (spectateProduct) {
                const gtmEnd = isOn ? SELECTION_GTM_REMOVE_BETSLIP : SELECTION_GTM_ADD_BETSLIP;
                const gtmStart = spectateProduct.split(';');
                gtmStart.pop();
                button.dataset.spectateProduct = `${gtmStart.join(';')};${gtmEnd}`;
            }
        });
};

const toggleSelections = (selectionIds) => {
    selectionIds.forEach(selectionId =>
        toggleSelection(selectionId, true));
};

export const addSelectionToBetslip = (selectionId, selectionFlags = {}, isReceiptAdd = false, selectionSelector = null) => {
    const state = betslipStore.getState();
    // for multi bet buttons, if the selection is already in the betslip, we remove the selection
    if (selectionSelector) {
        const selectionIds = selectUserSelectionIds(state);
        if (selectionIds.includes(selectionId)) {
            removeSelectionFromBetslip(selectionId, selectionSelector);

            return;
        }
    }
    const isReceiptShown = selectShowBetReceipt(state);
    const isMyBetsTab = selectIsMyBetsTab(state);
    const existingSelectionsCount = selectCountUserSelections(state);
    const maxSelections = selectBetslipMaxSelectionsConfig(state);

    if (existingSelectionsCount === Number(maxSelections)) {
        betslipStore.dispatch(triggerMaxSelectionsModal());

        return;
    }

    betslipStore.dispatch(
        addSelection({
            id: selectionId,
            ...selectionFlags,
        })
    );
    // for multi bet button, we need a different selector to highlight it
    if (selectionSelector) {
        toggleSelection(selectionSelector, true);
    }
    toggleSelection(selectionId, true);
    fetchBetslipData();
    if (isReceiptShown && !isReceiptAdd) {
        betslipStore.dispatch(goBackToBetslip());
    }

    if (isMyBetsTab) {
        betslipStore.dispatch(changeHeaderTab('betslip'));
    }
};

const filterAddedSelections = (ids) => {
    // first stringify and make ids unique
    const selectionsToAdd = [...(new Set(ids.map(String)))];

    const state = betslipStore.getState();
    const existingSelections = selectUserSelectionIds(state);

    return selectionsToAdd.filter(
        idToAdd => !existingSelections.includes(idToAdd),
    );
};

export const addSelectionsToBetslip = (idsToAdd, pinnedTab = '', selectionFlags = [], surveyId = null) => {
    const selectionIds= filterAddedSelections(idsToAdd);
    if (selectionFlags.length > 0) {
        selectionIds.forEach(selectionId => {
            const selectionFlag = selectionFlags.find(flag => flag.id === selectionId);
            addSelectionToBetslip(selectionId, selectionFlag, true);
        });
        betslipStore.dispatch(pinTab(pinnedTab));

        return;
    }
    betslipStore.dispatch(addSelections({
        selectionIds,
        surveyId,
    }));
    betslipStore.dispatch(pinTab(pinnedTab));

    toggleSelections(selectionIds, true);
    fetchBetslipData();
};

export const removeSelectionFromBetslip = (selectionId, selectionSelector = null) => {
    const count = selectCountUserSelections(betslipStore.getState());

    if (count > 1) {
        betslipStore.dispatch(
            removeSelection(selectionId)
        );
        if (selectionSelector) {
            toggleSelection(selectionSelector, false);
        } else {
            toggleSelection(selectionId, false);
        }
        fetchBetslipData();
    } else {
        // clear Betslip if customer removes the last selection
        clearBetslip(selectionId);
    }
};

export const clearBetslipSelections = (selectionId = null) => {
    const isBetReceipt = selectBetReceiptExists(betslipStore.getState());
    betslipStore.dispatch(
        clearSelections({ selectionId, isBetReceipt }),
    );
    toggleSelection(null, false);
};

export const clearBetslip = (selectionId = null) => {
    const uiMode = selectUiMode(betslipStore.getState());
    if (uiMode === 'baby') {
        betslipStore.dispatch(setFullScreenOff());
        setSiteScrollDisabled(false);
    }
    clearBetslipSelections(selectionId);
};

export const addFcTcToBetslip = (selections, selectionFlags = {}) => {
    const combinationToAdd = selections.map(({selectionId}) => selectionId);
    const existingCombination = findFcTcSelectionId(combinationToAdd);

    if (!existingCombination) {
        const fcTcSelectionId = combinationToAdd.join('_');
        addSelectionToBetslip(fcTcSelectionId, selectionFlags);
    }
};

export const updateBetslipOpenBetsCounter = (number, decrement = false) => {
    if (decrement) {
        betslipStore.dispatch(decrementOpenBets(number));
    } else {
        betslipStore.dispatch(updateOpenBets(number));
    }
};

export const setBetslipFullScreen = () => {
    const uiMode = selectUiMode(betslipStore.getState());
    if (uiMode === 'baby') {
        betslipStore.dispatch(setFullScreenOn());
        setSiteScrollDisabled(true);
    }
};

export const exitFullScreenBetslip = () => {
    const uiMode = selectUiMode(betslipStore.getState());
    if (uiMode === 'baby') {
        betslipStore.dispatch(setFullScreenOff());
        setSiteScrollDisabled(false);
    }
};

export const closeErrorModal = () => {
    betslipStore.dispatch(
        closeModal()
    );
    betslipStore.dispatch(
        goBackToBetslip()
    );
};

export const openBetslipMyBetsTab = () => {
    setBetslipFullScreen();
    betslipStore.dispatch(
        changeHeaderTab('my-bets')
    );
};

export const addToBetslipFromDeeplink = (selections, surveyId = null) => {
    addSelectionsToBetslip(selections, undefined, undefined, surveyId);
};

export const disableBetting = () => {
    betslipStore.dispatch(
        setBettingEnabled(false)
    );
};

export const enableBetting = () => {
    betslipStore.dispatch(
        setBettingEnabled(true)
    );
};
